/**
 * Connected to: what this points to, by what the link means (part of, starts · ends at,
 * same as), and what points at it, by class and relation. Each group opens into a list.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@bn-apps/dim-ui-react';
import type { IncomingLinks } from '@hooks/useIncomingLinks';
import { shortenUri } from '@hooks/useEntity';
import { listIncoming, type IncomingGroup } from '@services/entityView.service';
import type { EntityProperty } from '@/types';
import { leadingPredicate, setting } from '@utils/viewShapes.utils';
import { ERA } from '../page';
import { localName, words } from '../linearLocation';
import { recordName, UriLink, uriText } from '../widgets/values';
import { Chip } from '../widgets/chip';
import type { WidgetContext } from '../widgets/types';
import styles from '../EntityTemplate.module.scss';

const PAGE = 20;
/** At most this many more per click: a track has thousands of objects on it. */
const MORE = 200;
/** A class as words: railml3:SignalIS → Signal, bno:TrackSegment → Track segment. */
function className(iri: string, context: WidgetContext): string {
  const label = context.resolved[iri]?.[0];
  if (label && !label.includes(':')) return label;
  const text = words(localName(iri).replace(/(IS|IL)$/, '')).toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

interface MergedGroup extends IncomingGroup {
  label: string;
  relation: string;
  classes: string[];
}

function IncomingList({
  uri,
  group,
  context,
}: {
  uri: string;
  group: IncomingGroup;
  context: WidgetContext;
}): JSX.Element {
  const { t } = useTranslation();
  const [items, setItems] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);
  const load = (offset: number, size: number) => {
    setLoading(true);
    listIncoming(uri, group, offset, size)
      .then((page) => setItems((current) => [...(offset === 0 ? [] : (current ?? [])), ...page]))
      .catch(() => setItems((current) => current ?? []))
      .finally(() => setLoading(false));
  };
  // The merged groups are rebuilt on every render of the section: depend on what they query
  const { predicate, classIri } = group;
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listIncoming(uri, { predicate, classIri }, 0, PAGE)
      .then((page) => !cancelled && setItems(page))
      .catch(() => !cancelled && setItems([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [uri, predicate, classIri]);
  return (
    <div className={styles.groupBody}>
      <ul className={styles.linkList}>
        {(items ?? []).map((iri) => (
          <li key={iri}>
            <UriLink uri={iri} context={context} />
          </li>
        ))}
      </ul>
      {items && items.length < group.count && (
        <Button
          variant="tertiary"
          size="sm"
          isBusy={loading}
          onClick={() => load(items.length, Math.min(MORE, group.count - items.length))}
        >
          {group.count - items.length > MORE
            ? t('entity.template.connected.showMore', { count: MORE, total: group.count })
            : t('entity.template.connected.showAll', { count: group.count })}
        </Button>
      )}
    </div>
  );
}

interface ConnectedProps {
  uri: string;
  entityProperties: EntityProperty[];
  incoming: IncomingLinks;
  context: WidgetContext;
  designators: string[];
  onOpenGraph: (() => void) | null;
}

const Connected = ({
  uri,
  entityProperties,
  incoming,
  context,
  designators,
  onOpenGraph,
}: ConnectedProps): JSX.Element => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<string | null>(null);
  const toggle = (key: string) => setOpen(open === key ? null : key);
  const rows = context.model?.allProperties ?? [];
  const groupOrder = (row: { groupId: string | null }) =>
    (row.groupId ? context.model?.shapes?.groups.get(row.groupId)?.order : undefined) ??
    Number.MAX_SAFE_INTEGER;
  // Outgoing links: the rows with dview:link (its kind) and dview:icon, in the order of their groups.
  const linkRows = rows
    .filter((row) => setting(row, 'link') && leadingPredicate(row.path))
    .sort((a, b) => groupOrder(a) - groupOrder(b) || a.order - b.order);
  const kinds = [...new Set(linkRows.map((row) => setting(row, 'link')!))].map((kind) => ({
    kind,
    rows: linkRows.filter((row) => setting(row, 'link') === kind),
  }));
  const links = (kindRows: typeof linkRows) =>
    kindRows.flatMap((row) =>
      entityProperties
        .filter((property) => property.predicate === leadingPredicate(row.path))
        .flatMap((property) =>
          property.objects
            .filter((object) => object.type === 'uri' && object.value !== uri)
            .map((object) => ({
              iri: object.value,
              icon: setting(row, 'icon'),
              label: recordName(
                object.value,
                context,
                row.classIri
                  ? (context.resolved[row.classIri]?.[0] ?? localName(row.classIri))
                  : null
              ),
            }))
        )
    );
  // Incoming plumbing (kg-gen's helper nodes): the classes of the rows marked dview:plumbing.
  const hiddenClasses = new Set(
    rows.filter((row) => setting(row, 'plumbing') && row.classIri).map((row) => row.classIri!)
  );
  // The symbol of a class: from the rows that draw it (sh:class + dview:symbol).
  const glyphs = Object.fromEntries(
    rows
      .filter((row) => row.classIri && setting(row, 'symbol'))
      .map((row) => [row.classIri!, setting(row, 'symbol')!])
  );

  const outgoing = kinds
    .map((kind) => {
      const seen = new Set<string>();
      // The same place under several IRIs (Hamar three times) shows once, by its name.
      const unique = links(kind.rows).filter((link) => {
        const name = uriText(link.iri, context);
        if (seen.has(name)) return false;
        seen.add(name);
        return true;
      });
      return { kind: kind.kind, links: unique };
    })
    .filter((kind) => kind.links.length > 0);
  const rinfId = entityProperties.find((property) => property.predicate === `${ERA}trackId`)
    ?.objects[0]?.value;
  const sameAs = [...designators, ...(rinfId ? [`RINF ${rinfId}`] : [])];
  const relation = (predicate: string) =>
    t(`entity.template.connected.relation.${localName(predicate)}`, localName(predicate));
  // Classes kg-gen gives the same objects twice (SignalIS and SignalIL) read as one row.
  const groups: MergedGroup[] = [];
  for (const group of incoming.groups) {
    if (hiddenClasses.has(group.classIri)) continue;
    const label = className(group.classIri, context);
    const text = relation(group.predicate);
    const same = groups.find((existing) => existing.label === label && existing.relation === text);
    if (same) {
      same.classes.push(group.classIri);
      same.count = Math.max(same.count, group.count);
    } else {
      groups.push({ ...group, label, relation: text, classes: [group.classIri] });
    }
  }
  const total =
    outgoing.reduce((sum, kind) => sum + kind.links.length, 0) +
    groups.reduce((sum, group) => sum + group.count, 0);

  return (
    <section className={styles.section} aria-labelledby="entity-connected">
      <div className={styles.sectionHead}>
        <h2 id="entity-connected" className={styles.sectionTitle}>
          {t('entity.template.connected.title')}{' '}
          <span className={styles.titleCount}>
            {t('entity.template.connected.things', { count: total })}
          </span>
        </h2>
        {onOpenGraph && (
          <Button variant="tertiary" size="sm" onClick={onOpenGraph}>
            {t('entity.template.connected.openGraph')}
          </Button>
        )}
      </div>
      <div className={styles.connected}>
        <div>
          <p className={styles.columnLabel}>{t('entity.template.connected.outgoing')}</p>
          <div className={styles.groups}>
            {outgoing.map((kind) => (
              <div key={kind.kind} className={styles.grp}>
                <span className={styles.grpKind}>
                  {t(`entity.template.connected.kind.${kind.kind}`)}
                </span>
                <span className={styles.chips}>
                  {kind.links.slice(0, open === kind.kind ? undefined : 4).map((link) => (
                    <Chip
                      key={link.iri}
                      iri={link.iri}
                      icon={link.icon}
                      label={link.label}
                      context={context}
                    />
                  ))}
                  {kind.links.length > 4 && (
                    <Button variant="tertiary" size="xs" onClick={() => toggle(kind.kind)}>
                      {open === kind.kind
                        ? t('entity.template.facts.showFewer')
                        : `+${kind.links.length - 4}`}
                    </Button>
                  )}
                </span>
              </div>
            ))}
            {sameAs.length > 0 && (
              <div className={styles.grp}>
                <span className={styles.grpKind}>{t('entity.template.connected.kind.sameAs')}</span>
                <span className={styles.chips}>
                  {sameAs.map((text) => (
                    <Chip key={text} mono={text} context={context} />
                  ))}
                </span>
              </div>
            )}
          </div>
        </div>
        <div>
          <p className={styles.columnLabel}>{t('entity.template.connected.incoming')}</p>
          {incoming.status === 'loading' && (
            <p className={styles.cardNote}>{t('entity.template.connected.loading')}</p>
          )}
          {incoming.status === 'unavailable' && (
            <p className={styles.cardNote}>{t('entity.template.connected.unavailable')}</p>
          )}
          <div className={styles.groups}>
            {groups.map((group) => {
              const key = `${group.predicate} ${group.classIri}`;
              return (
                <div key={key} className={styles.grpWrap}>
                  <button
                    type="button"
                    className={styles.grp}
                    aria-expanded={open === key}
                    onClick={() => toggle(key)}
                  >
                    <span
                      className={styles.grpGlyph}
                      data-glyph={
                        group.classes.map((classIri) => glyphs[classIri]).find(Boolean) ?? 'other'
                      }
                      aria-hidden="true"
                    />
                    <span
                      title={group.classes
                        .map((iri) => shortenUri(iri, context.prefixes))
                        .join(' + ')}
                    >
                      {group.label}
                    </span>
                    <span className={styles.grpKind}>{group.relation}</span>
                    <b className={styles.grpCount}>{group.count}</b>
                    <span className={styles.chevron} aria-hidden="true">
                      {open === key ? '⌄' : '›'}
                    </span>
                  </button>
                  {open === key && <IncomingList uri={uri} group={group} context={context} />}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Connected;
